import React, { createContext, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { fetchAllExchangeProducts } from '../services/productService';

const STORE_API_URL = process.env.REACT_APP_API_URL;

// Create the ProductsContext
export const ProductsContext = createContext();

// ProductsProvider component
export const ProductsProvider = ({ children }) => {
  const { token, role } = useContext(AuthContext);
  const [productsArray, setProductsArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1); // For pagination handling

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const resetProducts = () => {
    console.log("Resetting products state");
    //setProductsArray([]);
    setProducts([]); // Clear the products array
    setCurrentPage(1); // Reset the current page
    setHasMore(true); // Reset the 'has more' flag
    setError(null); // Clear any existing errors
  };

  // Load more products function
  const loadMoreProducts = async (filterTags, limit = 10, page = null) => {
    //if (loading) return; // Prevent re-triggering if already loading

    // If a specific page is requested, use that; otherwise use currentPage
    const pageToLoad = page !== null ? page : currentPage;
    console.log(`Attempting to load page ${pageToLoad} with limit ${limit}`);
    setLoading(true);
    setError(null);

    try {
      const { products: newProducts, page: loadedPage } = await fetchAllExchangeProducts(pageToLoad, limit, filterTags);

      console.log(`Loaded ${newProducts.length} products for page ${loadedPage}`);
      
      // If loading first page, replace products; otherwise append
      if (pageToLoad === 1) {
        setProducts(newProducts);
        setCurrentPage(2); // Reset to page 2 for subsequent loads
      } else {
        setProducts((prevProducts) => [...prevProducts, ...newProducts]);
        setCurrentPage(loadedPage + 1); // Increment page for next load
      }

      // Determine if more products are available
      if (newProducts.length < limit) {
        console.log("No more products to load.");
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      // Explicitly return the fetched products
      return newProducts;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch products with pagination and optional type filter
  const loadAllProducts = async (storeId, type = '', page = 1, active) => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const url = role === 'super' 
        ? `${STORE_API_URL}/super/products` 
        : `${STORE_API_URL}/products`;

      console.log ('Loading all products...');
      const requestUrl = `${url}/store/${storeId}?page=${page}&type=${type || ''}&active=${active}`;
      console.log ('request Url: ' + requestUrl);

      const response = await fetch(requestUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // If the response is not OK, throw an error
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }

      // Parse the response to JSON
      const responseData = await response.json();
      console.log('Response from loadAllProducts: ', responseData);

      // Update the total pages state
      setTotalPages(responseData.total_pages);

      // Update products array based on whether it is the first page or a subsequent page
      if (page === 1) {
        setProductsArray(responseData.products);
      } else {
        setProductsArray((prevProducts) => [...prevProducts, ...responseData.products]);
      }

      // Return the response data so it can be handled by the calling component
      return responseData;

    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveProduct = async (productId, active) => {
    if (!token) return;

    setLoading(true);
    setError(null);
  
    try {
      const response = await fetch(`${STORE_API_URL}/products/archive/${productId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active }),
      });
  
      if (!response.ok) {
        throw new Error(`Error ${active ? 'resuming' : 'archiving'} product.`);
      }

      // Update the productsArray to reflect the archived status
      setProductsArray((prevProducts) =>
        prevProducts.map((product) =>
          product._id === productId ? { ...product, active } : product
        )
      );

      // Return a success flag
      return true;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        productsArray,
        loading,
        error,
        totalPages,
        hasMore, 
        setHasMore,
        setLoading,
        loadAllProducts,
        handleArchiveProduct,
        setCurrentPage, 
        loadMoreProducts, 
        resetProducts
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};